import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { hojeBR } from '../utils/data';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import { useAuth } from '../contexts/AuthContext';
import { configParticipante } from '../config/participantes';
import s from './Financeiro.module.css';

const fmtBRL = v => parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// agendado_para vem do banco em UTC — mostra sempre em horário de Brasília
// (UTC-3 fixo), igual ao resto do sistema (ver utils/data.js#hojeBR).
const hhmmBR = iso => {
  const d = new Date(new Date(iso).getTime() - 3 * 60 * 60 * 1000);
  return d.toISOString().slice(11, 16);
};
const dataBR = iso => {
  const d = new Date(new Date(iso).getTime() - 3 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
};
// Horário de término estimado (início + duração) — só pra exibição.
const hhmmFim = (iso, duracaoMin) => {
  const fim = new Date(new Date(iso).getTime() + (duracaoMin || 30) * 60000);
  return hhmmBR(fim.toISOString());
};
const chaveDia = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Ciclo de vida do agendamento: agendado (aguardando) → em_atendimento
// (cliente/animal chegou, pedido editável — itens/serviços podem mudar) →
// aprovado ("Concluído", atendimento terminou, liberado pro F2 do PDV) →
// importado ("Finalizado", venda já fechada no PDV).
const STATUS_LABEL = {
  agendado: 'Agendado',
  em_atendimento: 'Em Atendimento',
  aprovado: 'Concluído',
  importado: 'Finalizado',
  cancelado: 'Cancelado',
};
const STATUS_COR = {
  agendado:       { bg: '#fef3c7', cor: '#92400e', ponto: '#f59e0b', emoji: '🟡' },
  em_atendimento: { bg: '#dbeafe', cor: '#1e40af', ponto: '#3b82f6', emoji: '🔵' },
  aprovado:       { bg: '#dcfce7', cor: '#166534', ponto: '#22c55e', emoji: '🟢' },
  importado:      { bg: '#ede9fe', cor: '#5b21b6', ponto: '#8b5cf6', emoji: '🟣' },
  cancelado:      { bg: '#fee2e2', cor: '#991b1b', ponto: '#ef4444', emoji: '🔴' },
};

function BadgeStatus({ status }) {
  const cor = STATUS_COR[status] || STATUS_COR.agendado;
  return (
    <span className={s.badge} style={{ background: cor.bg, color: cor.cor, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 8 }}>{cor.emoji}</span>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function LegendaStatus() {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '2px 0 14px', fontSize: 11.5, color: 'var(--text-secondary)' }}>
      {Object.entries(STATUS_LABEL).map(([k, label]) => (
        <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span>{STATUS_COR[k].emoji}</span>
          {label}
        </span>
      ))}
    </div>
  );
}

// Contadores por status — usado no resumo do mês (topo) e no resumo do dia
// (cabeçalho da lista embaixo do calendário).
function contarPorStatus(lista) {
  const c = { agendado: 0, em_atendimento: 0, aprovado: 0, importado: 0, cancelado: 0 };
  for (const p of lista) c[p.status] = (c[p.status] || 0) + 1;
  return { total: lista.length, ...c };
}

function ResumoContadores({ resumo, tamanho = 12.5 }) {
  const itens = [
    resumo.agendado    > 0 && { label: `${resumo.agendado} agendado${resumo.agendado > 1 ? 's' : ''}`, cor: STATUS_COR.agendado },
    resumo.em_atendimento > 0 && { label: `${resumo.em_atendimento} em atendimento`, cor: STATUS_COR.em_atendimento },
    resumo.aprovado     > 0 && { label: `${resumo.aprovado} concluído${resumo.aprovado > 1 ? 's' : ''}`, cor: STATUS_COR.aprovado },
    resumo.importado    > 0 && { label: `${resumo.importado} finalizado${resumo.importado > 1 ? 's' : ''}`, cor: STATUS_COR.importado },
    resumo.cancelado    > 0 && { label: `${resumo.cancelado} cancelado${resumo.cancelado > 1 ? 's' : ''}`, cor: STATUS_COR.cancelado },
  ].filter(Boolean);

  if (!resumo.total) return null;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: tamanho }}>
      {itens.map((it, i) => <span key={i}>{it.cor.emoji} {it.label}</span>)}
    </span>
  );
}

// Cartõezinhos de indicador (Agendados/Em atendimento/Concluídos/
// Finalizados/Cancelados) — vai acima do calendário, é o "resumo do mês".
function ResumoCardsMes({ resumo }) {
  const cards = [
    { chave: 'agendado', label: 'Agendados' },
    { chave: 'em_atendimento', label: 'Em atendimento' },
    { chave: 'aprovado', label: 'Concluídos' },
    { chave: 'importado', label: 'Finalizados' },
    { chave: 'cancelado', label: 'Cancelados' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))', gap: 8, marginBottom: 10 }}>
      {cards.map(c => {
        const cor = STATUS_COR[c.chave];
        return (
          <div key={c.chave} style={{
            borderRadius: 10, padding: '9px 12px', background: cor.bg,
            border: `1px solid ${cor.ponto}55`,
          }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: cor.cor, whiteSpace: 'nowrap' }}>
              {cor.emoji} {c.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: cor.cor, marginTop: 1 }}>
              {resumo[c.chave]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Barra de ocupação — quanto da janela de horário (padrão 08h–20h) já está
// tomado pela soma de duração dos atendimentos não cancelados do dia.
const MINUTOS_JANELA_PADRAO = 12 * 60;

function BarraOcupacao({ lista, tamanho = 'normal' }) {
  const minutosOcupados = lista
    .filter(p => p.status !== 'cancelado')
    .reduce((s, p) => s + (parseInt(p.duracao_min_estimada) || 30), 0);
  const pct = Math.min(100, Math.round((minutosOcupados / MINUTOS_JANELA_PADRAO) * 100));
  const cor = pct >= 80 ? '#ef4444' : pct >= 50 ? '#f59e0b' : '#22c55e';
  const compacto = tamanho === 'compacto';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: compacto ? 10.5 : 11.5, marginBottom: 3 }}>
        <span style={{ color: compacto ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)' }}>
          {compacto ? 'Ocupação' : '📊 Ocupação da agenda'}
        </span>
        <strong style={{ color: compacto ? '#fff' : 'var(--text)' }}>{pct}% ocupada</strong>
      </div>
      <div style={{ height: compacto ? 6 : 8, borderRadius: 6, background: compacto ? 'rgba(255,255,255,0.15)' : 'var(--border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: cor, borderRadius: 6, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Calendário do mês — visão geral, clica no dia pra selecionar
   ──────────────────────────────────────────────────────── */
const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const NOMES_MES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
  'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

function gerarGradeMes(ano, mes) { // mes: 0-11
  const primeiroDia = new Date(ano, mes, 1);
  const ultimoDiaNum = new Date(ano, mes + 1, 0).getDate();
  const dias = [];
  for (let i = primeiroDia.getDay(); i > 0; i--) dias.push({ data: new Date(ano, mes, 1 - i), foraDoMes: true });
  for (let d = 1; d <= ultimoDiaNum; d++) dias.push({ data: new Date(ano, mes, d), foraDoMes: false });
  while (dias.length % 7 !== 0) {
    const prox = new Date(dias[dias.length - 1].data);
    prox.setDate(prox.getDate() + 1);
    dias.push({ data: prox, foraDoMes: true });
  }
  return dias;
}

function CalendarioMes({ ano, mes, porDia, selecionado, onSelecionar, onMudarMes, onHoje }) {
  const grade = useMemo(() => gerarGradeMes(ano, mes), [ano, mes]);
  const hojeChave = hojeBR();

  return (
    <div style={{
      padding: '10px 12px', marginBottom: 10, borderRadius: 12,
      background: 'linear-gradient(180deg, var(--primary-light) 0%, var(--card-bg) 70px)',
      border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 16 }}>🗓️</span>
        <button className={s.btnErp} onClick={() => onMudarMes(-1)}>◀</button>
        <strong style={{ minWidth: 160, textAlign: 'center', fontSize: 14, color: 'var(--primary-dark)' }}>
          {NOMES_MES[mes]} de {ano}
        </strong>
        <button className={s.btnErp} onClick={() => onMudarMes(1)}>▶</button>
        <button className={s.btnErp} onClick={onHoje}>Hoje</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {DIAS_SEMANA.map((d, i) => (
          <div key={d} style={{
            fontSize: 10.5, fontWeight: 800, textAlign: 'center', padding: '3px 0', borderRadius: 5,
            color: (i === 0 || i === 6) ? '#fff' : 'var(--primary-dark)',
            background: (i === 0 || i === 6) ? 'var(--primary)' : 'var(--primary-light)',
          }}>
            {d}
          </div>
        ))}
        {grade.map(({ data: d, foraDoMes }, i) => {
          const chave = chaveDia(d);
          const eventos = porDia[chave] || [];
          const eSelecionado = chave === selecionado;
          const eHoje = chave === hojeChave;
          const finalDeSemana = d.getDay() === 0 || d.getDay() === 6;
          const tituloResto = eventos.slice(2)
            .map(ev => `${hhmmBR(ev.agendado_para)} ${ev.participante_nome || ev.cliente_nome || ''}`).join('\n');
          return (
            <div key={chave}
              onClick={() => onSelecionar(chave)}
              style={{
                minHeight: 60, borderRadius: 8, padding: '4px 5px', cursor: 'pointer',
                transition: 'transform 0.08s',
                background: eSelecionado
                  ? 'var(--primary-light)'
                  : eHoje
                    ? '#fff7ed'
                    : finalDeSemana && !foraDoMes
                      ? '#fdf4ff'
                      : 'var(--card-bg)',
                border: eSelecionado ? '3px solid var(--primary)' : eHoje ? '1.5px solid #fb923c' : '1px solid var(--border)',
                opacity: foraDoMes ? 0.4 : 1,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                <span style={{
                  fontSize: eSelecionado ? 16 : 12, fontWeight: eHoje || eSelecionado ? 800 : 600,
                  color: eSelecionado ? 'var(--primary-dark)' : eHoje ? '#c2410c' : 'var(--text)',
                  lineHeight: 1,
                }}>
                  {d.getDate()}
                </span>
                {eHoje && (
                  <span style={{
                    fontSize: 9, fontWeight: 800, color: '#fff', background: '#fb923c',
                    borderRadius: 4, padding: '2px 5px', letterSpacing: 0.3,
                  }}>
                    HOJE
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {eventos.slice(0, 2).map(ev => {
                  const cor = STATUS_COR[ev.status] || STATUS_COR.agendado;
                  const quem = ev.participante_nome || ev.cliente_nome || '';
                  return (
                    <div key={ev.id} title={`${hhmmBR(ev.agendado_para)} — ${quem} (${STATUS_LABEL[ev.status] || ev.status})`}
                      style={{
                        fontSize: 10, background: cor.bg,
                        color: cor.cor, borderRadius: 4, fontWeight: 600,
                        padding: '1px 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                      {hhmmBR(ev.agendado_para)} {ev.participante_nome ? '🐾 ' : '👤 '}{quem}
                    </div>
                  );
                })}
                {eventos.length > 2 && (
                  <div title={tituloResto} style={{ fontSize: 10, fontWeight: 700, color: 'var(--primary)', cursor: 'default' }}>
                    +{eventos.length - 2}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Linha do tempo do dia — visão rápida de quão cheia está a agenda
   selecionada (barras proporcionais à duração de cada atendimento).
   ──────────────────────────────────────────────────────── */
const PX_POR_HORA = 46;

function LinhaDoTempoDia({ listaDia }) {
  if (!listaDia.length) return null;

  // Faixa de horas a mostrar: padrão 08h–20h, mas estica se algum
  // agendamento cair fora disso (não corta atendimento de fora do horário).
  let horaMin = 8, horaMax = 20;
  for (const p of listaDia) {
    const d = new Date(new Date(p.agendado_para).getTime() - 3 * 60 * 60 * 1000);
    const h = d.getUTCHours();
    if (h < horaMin) horaMin = h;
    if (h + 1 > horaMax) horaMax = h + 1;
  }
  const totalHoras = horaMax - horaMin;
  const horasArr = Array.from({ length: totalHoras + 1 }, (_, i) => horaMin + i);

  const minutosDesdeInicio = (iso) => {
    const d = new Date(new Date(iso).getTime() - 3 * 60 * 60 * 1000);
    return (d.getUTCHours() - horaMin) * 60 + d.getUTCMinutes();
  };

  return (
    <div style={{
      borderRadius: 12, border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)',
      padding: '12px 16px 16px', marginBottom: 14, background: 'var(--card-bg)',
    }}>
      <strong style={{ fontSize: 13, color: 'var(--primary-dark)' }}>⏱️ Linha do tempo do dia</strong>
      <div style={{ position: 'relative', marginTop: 12, marginLeft: 46, height: totalHoras * PX_POR_HORA + 4 }}>
        {horasArr.map(h => (
          <div key={h} style={{
            position: 'absolute', top: (h - horaMin) * PX_POR_HORA, left: -46, right: 0,
            borderTop: '1px dashed var(--border)',
          }}>
            <span style={{
              position: 'relative', top: -7, background: 'var(--card-bg)', paddingRight: 6,
              fontSize: 10.5, color: 'var(--text-muted)', fontWeight: 600,
            }}>
              {String(h).padStart(2, '0')}:00
            </span>
          </div>
        ))}
        {listaDia.map(p => {
          const top = minutosDesdeInicio(p.agendado_para) * (PX_POR_HORA / 60);
          const duracao = parseInt(p.duracao_min_estimada) || 30;
          const altura = Math.max(16, duracao * (PX_POR_HORA / 60) - 2);
          const cor = STATUS_COR[p.status] || STATUS_COR.agendado;
          const quem = p.participante_nome || p.cliente_nome || '—';
          return (
            <div key={p.id}
              title={`${hhmmBR(p.agendado_para)} — ${quem} (${duracao} min, ${STATUS_LABEL[p.status] || p.status})`}
              style={{
                position: 'absolute', top, height: altura, left: 4, right: 4,
                background: cor.bg, borderLeft: `3px solid ${cor.ponto}`, borderRadius: 5,
                padding: '1px 7px', fontSize: 11, color: cor.cor, fontWeight: 700,
                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                display: 'flex', alignItems: 'center',
              }}>
              {hhmmBR(p.agendado_para)} {quem}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Seletor de itens (Serviços + Produtos) — usado tanto pra criar quanto
   pra editar um agendamento em atendimento.
   ──────────────────────────────────────────────────────── */
function SeletorServicos({ servicos, selecionados, onToggle }) {
  if (!servicos.length) {
    return (
      <span style={{ fontSize: 12, color: '#888' }}>
        Nenhum produto do tipo "Serviço" cadastrado — cadastre em Cad. Produtos.
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 140, overflowY: 'auto' }}>
      {servicos.map(p => (
        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
          <input type="checkbox" className={s.check}
            checked={!!selecionados[p.id]} onChange={() => onToggle(p.id)} />
          {p.nome} — R$ {fmtBRL(p.preco_venda)}
          {p.duracao_min ? ` (${p.duracao_min} min)` : ''}
        </label>
      ))}
    </div>
  );
}

function BuscaProdutos({ onAdicionar }) {
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);

  const buscar = async () => {
    if (!busca.trim()) return;
    setBuscando(true);
    try {
      const { data } = await api.get('/produtos', { params: { busca: busca.trim(), todos: 'true', limite: 20 } });
      setResultados((data?.rows || data || []).filter(p => p.tipo !== 'serviço'));
    } catch { setResultados([]); }
    finally { setBuscando(false); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 6 }}>
        <input className={s.input} style={{ flex: 1 }}
          value={busca} onChange={e => setBusca(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && buscar()}
          placeholder="Nome ou código do produto... e Enter" />
        <button type="button" className={s.btnErp} onClick={buscar}>{buscando ? '...' : 'Buscar'}</button>
      </div>
      {resultados.length > 0 && (
        <table className={s.tabela} style={{ marginTop: 4 }}>
          <tbody>
            {resultados.map(p => (
              <tr key={p.id} onClick={() => { onAdicionar(p); setResultados([]); setBusca(''); }}>
                <td>{p.nome}</td>
                <td style={{ width: 100, textAlign: 'right' }}>R$ {fmtBRL(p.preco_venda)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function TabelaItensEditavel({ itens, onQtd, onRemover }) {
  if (!itens.length) return null;
  return (
    <table className={s.tabela} style={{ marginTop: 6 }}>
      <thead><tr><th>Item</th><th style={{ width: 70 }}>Qtd</th><th style={{ width: 90 }}>Subtotal</th><th style={{ width: 30 }}></th></tr></thead>
      <tbody>
        {itens.map((i, idx) => (
          <tr key={i.produto_id ?? idx}>
            <td>{i.descricao}</td>
            <td>
              <input type="number" className={s.input} style={{ width: 60, padding: '3px 6px' }}
                value={i.quantidade} min="0.001" step="1"
                onChange={e => onQtd(idx, e.target.value)}
                onClick={e => e.stopPropagation()} />
            </td>
            <td>R$ {fmtBRL(i.subtotal)}</td>
            <td>
              <button type="button" className={s.btnPerigo} style={{ padding: '2px 7px' }}
                onClick={() => onRemover(idx)}>✕</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ────────────────────────────────────────────────────────
   Modal — Novo Agendamento
   ──────────────────────────────────────────────────────── */
function ModalNovoAgendamento({ dataInicial, onClose, onCriado }) {
  const { usuario } = useAuth();
  const cfgParticipante = configParticipante(usuario?.segmento_nome);
  const [busca, setBusca]           = useState('');
  const [sugestoes, setSugestoes]   = useState(null);
  const [cliente, setCliente]       = useState(null);

  const [participantes, setParticipantes] = useState([]);
  const [participanteId, setParticipanteId] = useState('');
  const [novoParticipante, setNovoParticipante] = useState(false);
  const [npNome, setNpNome] = useState('');
  const [npTipo, setNpTipo] = useState('');

  const [servicos, setServicos]     = useState([]);
  const [selecionados, setSelecionados] = useState({}); // { produto_id: true }

  // Produtos (mercadoria) — separado dos Serviços de propósito: na hora de
  // finalizar a venda (F2 do PDV), o sistema já separa automaticamente
  // mercadoria (NFC-e/NF-e) de serviço (NFS-e) por item — aqui só precisa
  // deixar incluir os dois tipos no mesmo agendamento.
  const [produtosAdd, setProdutosAdd] = useState([]);

  const [data, setData] = useState(dataInicial || hojeBR());
  const [hora, setHora] = useState('09:00');
  const [observacoes, setObservacoes] = useState('');

  const [erro, setErro]         = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    api.get('/produtos', { params: { tipo: 'serviço', todos: 'true', limite: 200 } })
      .then(r => setServicos(r.data?.rows || r.data || []))
      .catch(() => setServicos([]));
  }, []);

  const buscarClientes = async () => {
    setErro(''); setSugestoes(null);
    if (!busca.trim()) return;
    try {
      const { data } = await api.get('/clientes', { params: { busca: busca.trim(), todos: 'true' } });
      setSugestoes(data);
    } catch { setErro('Erro ao buscar clientes.'); }
  };

  const selecionarCliente = async (c) => {
    setCliente(c);
    setSugestoes(null);
    setBusca(c.nome);
    setParticipanteId('');
    setNovoParticipante(false);
    try {
      const { data } = await api.get(`/clientes/${c.id}/participantes`);
      setParticipantes(data);
    } catch { setParticipantes([]); }
  };

  const criarParticipante = async () => {
    if (!npNome.trim()) return;
    try {
      const { data } = await api.post(`/clientes/${cliente.id}/participantes`, { nome: npNome.trim(), tipo: npTipo.trim() || null });
      setParticipantes(p => [...p, data]);
      setParticipanteId(String(data.id));
      setNovoParticipante(false);
      setNpNome(''); setNpTipo('');
    } catch (e) { setErro(e.response?.data?.erro || 'Erro ao cadastrar participante.'); }
  };

  const toggleServico = (id) => setSelecionados(sel => ({ ...sel, [id]: !sel[id] }));

  const adicionarProduto = (p) => {
    setProdutosAdd(lista => {
      const existe = lista.find(i => i.produto_id === p.id);
      if (existe) return lista.map(i => i.produto_id === p.id
        ? { ...i, quantidade: i.quantidade + 1, subtotal: (i.quantidade + 1) * i.preco_unitario }
        : i);
      return [...lista, {
        produto_id: p.id, codigo_produto: p.codigo_interno, descricao: p.nome,
        unidade: p.unidade || 'UN', quantidade: 1, preco_unitario: p.preco_venda,
        desconto_perc: 0, subtotal: p.preco_venda,
      }];
    });
  };

  const alterarQtdProduto = (idx, qtd) => {
    const q = Math.max(0.001, parseFloat(qtd) || 1);
    setProdutosAdd(lista => lista.map((i, iidx) => iidx === idx
      ? { ...i, quantidade: q, subtotal: parseFloat((q * i.preco_unitario).toFixed(2)) }
      : i));
  };

  const removerProduto = (idx) => setProdutosAdd(lista => lista.filter((_, iidx) => iidx !== idx));

  const salvar = async () => {
    setErro('');
    if (!cliente) return setErro('Selecione o cliente.');
    if (!data || !hora) return setErro('Informe a data e a hora do agendamento.');
    const itensServicos = servicos
      .filter(p => selecionados[p.id])
      .map(p => ({
        produto_id: p.id, codigo_produto: p.codigo_interno, descricao: p.nome,
        unidade: p.unidade || 'UN', quantidade: 1,
        preco_unitario: p.preco_venda, desconto_perc: 0, subtotal: p.preco_venda,
      }));
    const itens = [...itensServicos, ...produtosAdd];
    if (!itens.length) return setErro('Selecione ao menos um serviço ou produto.');

    setSalvando(true);
    try {
      await api.post('/vendas/pedidos', {
        cliente_id: cliente.id,
        participante_id: participanteId ? parseInt(participanteId) : null,
        status: 'agendado',
        agendado_para: `${data}T${hora}:00-03:00`,
        observacoes: observacoes || null,
        itens,
      });
      onCriado();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Não foi possível criar o agendamento.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className={s.overlay} data-enter-scope="off">
      <div className={s.modal} style={{ width: 600, maxHeight: '88vh', overflowY: 'auto' }}>
        <div className={s.modalTitle}>Novo Agendamento</div>
        <div className={s.modalBody} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {erro && <div className={s.erroMsg}>{erro}</div>}

          {/* Cliente */}
          <div className={s.formGrupo}>
            <span className={s.label}>Cliente*</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input className={s.input} style={{ flex: 1 }} autoFocus
                value={busca}
                onChange={e => { setBusca(e.target.value); setCliente(null); }}
                onKeyDown={e => e.key === 'Enter' && buscarClientes()}
                placeholder="Nome, CPF/CNPJ ou telefone... e Enter" />
              <button type="button" className={s.btnErp} onClick={buscarClientes}>Buscar</button>
            </div>
            {sugestoes !== null && !cliente && (
              <table className={s.tabela} style={{ marginTop: 4 }}>
                <tbody>
                  {sugestoes.length === 0 && (
                    <tr><td style={{ color: '#888', fontStyle: 'italic' }}>Nenhum cliente encontrado</td></tr>
                  )}
                  {sugestoes.map(c => (
                    <tr key={c.id} onClick={() => selecionarCliente(c)}>
                      <td>{c.nome}</td>
                      <td style={{ width: 130 }}>{c.cpf_cnpj || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Participante */}
          {cliente && (
            <div className={s.formGrupo}>
              <span className={s.label}>{cfgParticipante.labelSingular} ({cfgParticipante.tipoPlaceholder})</span>
              {!novoParticipante ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <select className={s.select} style={{ flex: 1 }}
                    value={participanteId} onChange={e => setParticipanteId(e.target.value)}>
                    <option value="">— nenhum —</option>
                    {participantes.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}{p.tipo ? ` (${p.tipo})` : ''}</option>
                    ))}
                  </select>
                  <button type="button" className={s.btnErp} onClick={() => setNovoParticipante(true)}>+ Novo</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className={s.input} style={{ flex: 1 }} placeholder={cfgParticipante.nomePlaceholder}
                    value={npNome} onChange={e => setNpNome(e.target.value)} />
                  <input className={s.input} style={{ width: 150 }} placeholder={cfgParticipante.tipoPlaceholder}
                    value={npTipo} onChange={e => setNpTipo(e.target.value)} />
                  <button type="button" className={s.btnErp} onClick={criarParticipante}>Salvar</button>
                  <button type="button" className={s.btnErp} onClick={() => setNovoParticipante(false)}>✕</button>
                </div>
              )}
            </div>
          )}

          {/* Data/Hora */}
          <div className={s.formRow}>
            <div className={s.formGrupo}>
              <span className={s.label}>Data*</span>
              <input type="date" className={s.input} value={data} onChange={e => setData(e.target.value)} />
            </div>
            <div className={s.formGrupo}>
              <span className={s.label}>Hora*</span>
              <input type="time" className={s.input} value={hora} onChange={e => setHora(e.target.value)} />
            </div>
          </div>

          {/* Serviços */}
          <div className={s.formGrupo}>
            <span className={s.label}>Serviços</span>
            <SeletorServicos servicos={servicos} selecionados={selecionados} onToggle={toggleServico} />
          </div>

          {/* Produtos */}
          <div className={s.formGrupo}>
            <span className={s.label}>Produtos (opcional — ex.: ração, item vendido junto do atendimento)</span>
            <BuscaProdutos onAdicionar={adicionarProduto} />
            <TabelaItensEditavel itens={produtosAdd} onQtd={alterarQtdProduto} onRemover={removerProduto} />
          </div>

          {/* Observações */}
          <div className={s.formGrupo}>
            <span className={s.label}>Observações</span>
            <textarea className={s.input} style={{ minHeight: 50 }}
              value={observacoes} onChange={e => setObservacoes(e.target.value)} />
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={salvando} onClick={salvar}>
            {salvando ? 'Agendando...' : 'Agendar'}
          </button>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Modal — Editar itens (só durante "Em Atendimento")
   ──────────────────────────────────────────────────────── */
function ModalEditarItens({ pedido, onClose, onSalvo }) {
  const [itens, setItens]       = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [servicos, setServicos] = useState([]);
  const [erro, setErro]         = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/vendas/pedidos/${pedido.id}`),
      api.get('/produtos', { params: { tipo: 'serviço', todos: 'true', limite: 200 } }),
    ]).then(([pedidoRes, servRes]) => {
      setItens((pedidoRes.data.itens || []).map(i => ({
        produto_id: i.produto_id, codigo_produto: i.codigo_produto, descricao: i.descricao,
        unidade: i.unidade, quantidade: parseFloat(i.quantidade), preco_unitario: parseFloat(i.preco_unitario),
        desconto_perc: parseFloat(i.desconto_perc) || 0, subtotal: parseFloat(i.subtotal),
        tipo_item: i.tipo_item,
      })));
      setServicos(servRes.data?.rows || servRes.data || []);
    }).catch(() => setErro('Erro ao carregar o agendamento.'))
      .finally(() => setCarregando(false));
  }, [pedido.id]);

  const servicosSelecionados = useMemo(() =>
    Object.fromEntries(itens.filter(i => i.produto_id).map(i => [i.produto_id, true])),
    [itens]);

  const toggleServico = (servico) => {
    setItens(lista => {
      const jaTem = lista.some(i => i.produto_id === servico.id);
      if (jaTem) return lista.filter(i => i.produto_id !== servico.id);
      return [...lista, {
        produto_id: servico.id, codigo_produto: servico.codigo_interno, descricao: servico.nome,
        unidade: servico.unidade || 'UN', quantidade: 1, preco_unitario: servico.preco_venda,
        desconto_perc: 0, subtotal: servico.preco_venda,
      }];
    });
  };

  const adicionarProduto = (p) => {
    setItens(lista => {
      const existe = lista.find(i => i.produto_id === p.id);
      if (existe) return lista.map(i => i.produto_id === p.id
        ? { ...i, quantidade: i.quantidade + 1, subtotal: (i.quantidade + 1) * i.preco_unitario }
        : i);
      return [...lista, {
        produto_id: p.id, codigo_produto: p.codigo_interno, descricao: p.nome,
        unidade: p.unidade || 'UN', quantidade: 1, preco_unitario: p.preco_venda,
        desconto_perc: 0, subtotal: p.preco_venda,
      }];
    });
  };

  const alterarQtd = (idx, qtd) => {
    const q = Math.max(0.001, parseFloat(qtd) || 1);
    setItens(lista => lista.map((i, iidx) => iidx === idx
      ? { ...i, quantidade: q, subtotal: parseFloat((q * i.preco_unitario).toFixed(2)) }
      : i));
  };

  const removerItem = (idx) => setItens(lista => lista.filter((_, iidx) => iidx !== idx));

  const salvar = async () => {
    setErro('');
    if (!itens.length) return setErro('O agendamento precisa ter ao menos um item.');
    setSalvando(true);
    try {
      await api.put(`/vendas/pedidos/${pedido.id}`, { itens });
      onSalvo();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao salvar os itens.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className={s.overlay} data-enter-scope="off">
      <div className={s.modal} style={{ width: 560, maxHeight: '86vh', overflowY: 'auto' }}>
        <div className={s.modalTitle}>Editar Itens — {pedido.cliente_nome || 'Agendamento'} #{pedido.numero || pedido.id}</div>
        <div className={s.modalBody} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {erro && <div className={s.erroMsg}>{erro}</div>}
          {carregando ? (
            <div style={{ color: '#888', fontSize: 12 }}>Carregando...</div>
          ) : (
            <>
              <div className={s.formGrupo}>
                <span className={s.label}>Itens do atendimento</span>
                <TabelaItensEditavel itens={itens} onQtd={alterarQtd} onRemover={removerItem} />
                {!itens.length && <span style={{ fontSize: 12, color: '#888' }}>Nenhum item ainda.</span>}
              </div>

              <div className={s.formGrupo}>
                <span className={s.label}>+ Serviços</span>
                <SeletorServicos servicos={servicos} selecionados={servicosSelecionados} onToggle={id => toggleServico(servicos.find(sv => sv.id === id))} />
              </div>

              <div className={s.formGrupo}>
                <span className={s.label}>+ Produtos</span>
                <BuscaProdutos onAdicionar={adicionarProduto} />
              </div>
            </>
          )}
        </div>
        <div className={s.modalFooter}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={salvando || carregando} onClick={salvar}>
            {salvando ? 'Salvando...' : 'Salvar Itens'}
          </button>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Página — Agenda
   ──────────────────────────────────────────────────────── */
export default function Agenda() {
  const { usuario } = useAuth();
  const hoje = hojeBR();
  const [ano, setAno]         = useState(parseInt(hoje.slice(0, 4)));
  const [mes, setMes]         = useState(parseInt(hoje.slice(5, 7)) - 1); // 0-11
  const [data, setData]       = useState(hoje); // dia selecionado (lista embaixo)
  const [listaMes, setListaMes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]       = useState('');
  const [modalNovo, setModalNovo] = useState(false);
  const [editandoItens, setEditandoItens] = useState(null); // pedido | null

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const primeiro = `${ano}-${String(mes + 1).padStart(2, '0')}-01`;
      const ultimoDiaNum = new Date(ano, mes + 1, 0).getDate();
      const ultimo = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(ultimoDiaNum).padStart(2, '0')}`;
      const { data: rows } = await api.get('/vendas/pedidos', {
        params: {
          agendado_de:  `${primeiro}T00:00:00-03:00`,
          agendado_ate: `${ultimo}T23:59:59-03:00`,
          limite: 500,
        },
      });
      setListaMes(rows);
    } catch {
      setErro('Erro ao carregar a agenda do mês.');
    } finally {
      setCarregando(false);
    }
  }, [ano, mes]);

  useEffect(() => { carregar(); }, [carregar]);

  const porDia = useMemo(() => {
    const grupos = {};
    for (const p of listaMes) {
      const k = dataBR(p.agendado_para);
      (grupos[k] ||= []).push(p);
    }
    return grupos;
  }, [listaMes]);

  const listaDia = useMemo(() =>
    (porDia[data] || []).slice().sort((a, b) => a.agendado_para.localeCompare(b.agendado_para)),
    [porDia, data]);

  const resumoMes = useMemo(() => contarPorStatus(listaMes), [listaMes]);
  const resumoDia = useMemo(() => contarPorStatus(listaDia), [listaDia]);

  // Ao trocar de data (ou carregar a página) a área rolável já abre no fim
  // — mostra a lista de agendamentos do dia direto, sem precisar rolar
  // manualmente passando pela linha do tempo. rAF garante que o DOM já
  // recalculou a altura com o conteúdo do dia novo antes de rolar.
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [data, listaDia]);

  const mudarMes = (delta) => {
    let novoMes = mes + delta, novoAno = ano;
    if (novoMes < 0) { novoMes = 11; novoAno--; }
    if (novoMes > 11) { novoMes = 0; novoAno++; }
    setMes(novoMes); setAno(novoAno);
  };

  const irParaHoje = () => {
    setAno(parseInt(hoje.slice(0, 4)));
    setMes(parseInt(hoje.slice(5, 7)) - 1);
    setData(hoje);
  };

  const selecionarDia = (chave) => {
    setData(chave);
    // Se o clique mudou de mês (dia "fora do mês" no grid), acompanha
    const [a, m] = chave.split('-').map(Number);
    if (a !== ano || (m - 1) !== mes) { setAno(a); setMes(m - 1); }
  };

  const iniciarAtendimento = async (pedido) => {
    try {
      await api.patch(`/vendas/pedidos/${pedido.id}/iniciar-atendimento`);
      carregar();
    } catch (e) { setErro(e.response?.data?.erro || 'Erro ao iniciar atendimento.'); }
  };

  const concluirAtendimento = async (pedido) => {
    try {
      await api.patch(`/vendas/pedidos/${pedido.id}/concluir-atendimento`);
      carregar();
    } catch (e) { setErro(e.response?.data?.erro || 'Erro ao concluir atendimento.'); }
  };

  const cancelarAgendamento = async (pedido) => {
    if (!window.confirm(`Cancelar o agendamento #${pedido.id} de ${pedido.cliente_nome || 'cliente'}?`)) return;
    const senha = await pedirSenhaAdmin(
      `Cancelar o agendamento #${pedido.id}.`,
      { acao: 'cancelar_pedido', detalhes: `Agendamento #${pedido.id} — cliente: ${pedido.cliente_nome || '—'}` });
    if (senha === null) return;
    try {
      await api.patch(`/vendas/pedidos/${pedido.id}/cancelar`, { senha_admin: senha });
      carregar();
    } catch (e) { setErro(e.response?.data?.erro || 'Erro ao cancelar agendamento.'); }
  };

  // Segmento da organização não habilita Agendamento — o menu já não
  // aparece pra chegar aqui, mas protege quem digitar a URL direto.
  if (!usuario?.mostra_agendamento) {
    return (
      <div className={s.erpWrap}>
        <div className={s.titleBar}><span>📅</span> Agenda</div>
        <div className={s.conteudo} style={{ color: '#888', fontSize: 13, padding: 24, textAlign: 'center' }}>
          O segmento desta organização não usa Agendamento (configurável em Administrativo → Minha Empresa → Segmento).
        </div>
      </div>
    );
  }

  const dataFmt = new Date(`${data}T12:00:00-03:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });

  return (
    <div className={s.erpWrap} style={{ overflow: 'hidden' }}>
      <div className={s.titleBar}><span>📅</span> Agenda</div>

      {erro && <div className={s.erroMsg}>{erro}</div>}

      {/* Topo fixo — calendário sempre visível, não rola com o resto */}
      <div style={{ flexShrink: 0 }}>
        {carregando && !listaMes.length ? (
          <div style={{ color: '#888', fontSize: 12, marginBottom: 10 }}>Carregando...</div>
        ) : (
          <>
            <ResumoCardsMes resumo={resumoMes} />
            <CalendarioMes ano={ano} mes={mes} porDia={porDia} selecionado={data}
              onSelecionar={selecionarDia} onMudarMes={mudarMes} onHoje={irParaHoje} />
          </>
        )}
      </div>

      {/* Área rolável — legenda, linha do tempo e lista do dia; scrollbar
          própria em vez de arrastar o calendário junto. Abre por padrão já
          rolada até o fim (lista do dia), e o cabeçalho com o botão "+ Novo
          Agendamento" fica grudado (sticky) no topo dela — some com o
          overflow:hidden do card antigo, que impedia sticky de funcionar. */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
      <LegendaStatus />

      <LinhaDoTempoDia listaDia={listaDia} />

      <div style={{
        position: 'sticky', top: 0, zIndex: 5,
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', flexWrap: 'wrap',
        background: 'var(--primary)', color: '#fff', borderRadius: '12px 12px 0 0',
        boxShadow: 'var(--shadow-sm)',
      }}>
        <span style={{ fontSize: 16 }}>📌</span>
        <strong style={{ textTransform: 'capitalize', fontSize: 14.5 }}>{dataFmt}</strong>
        {resumoDia.total > 0 && (
          <span style={{ fontSize: 12, opacity: 0.9 }}>
            — {resumoDia.total} atendimento{resumoDia.total > 1 ? 's' : ''}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button className={s.btnErp} style={{ background: '#fff' }} onClick={() => setModalNovo(true)}>
          + Novo Agendamento
        </button>
      </div>

      <div style={{
        borderRadius: '0 0 12px 12px', border: '1px solid var(--border)', borderTop: 'none',
        boxShadow: 'var(--shadow-sm)', overflow: 'hidden', minHeight: 220,
      }}>
        {resumoDia.total > 0 && (
          <div style={{
            padding: '10px 16px', background: 'var(--primary-light)', borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
          }}>
            <ResumoContadores resumo={resumoDia} />
            <div style={{ flex: '1 1 160px', minWidth: 160, maxWidth: 260 }}>
              <BarraOcupacao lista={listaDia} />
            </div>
          </div>
        )}

        <div className={s.conteudo} style={{ background: 'var(--card-bg)' }}>
        {!listaDia.length ? (
          <div style={{ color: '#888', fontSize: 12, padding: 20, textAlign: 'center' }}>
            Nenhum agendamento neste dia.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {listaDia.map(p => {
              const cor = STATUS_COR[p.status] || STATUS_COR.agendado;
              return (
                <div key={p.id} style={{
                  borderRadius: 10, border: '1px solid var(--border)', borderLeft: `4px solid ${cor.ponto}`,
                  padding: '10px 14px', background: 'var(--card-bg)', boxShadow: 'var(--shadow-sm)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 19, fontWeight: 800, color: 'var(--primary-dark)' }}>
                      {hhmmBR(p.agendado_para)}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      → {hhmmFim(p.agendado_para, p.duracao_min_estimada)} · ⏳ {p.duracao_min_estimada || 30} min
                    </span>
                    <BadgeStatus status={p.status} />
                    <span style={{ color: 'var(--text-muted)', fontSize: 11.5 }}>#{p.numero || p.id}</span>
                  </div>

                  <div style={{ marginTop: 6 }}>
                    {p.participante_nome ? (
                      <>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>
                          🐾 {p.participante_nome}{p.participante_tipo ? ` (${p.participante_tipo})` : ''}
                        </div>
                        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                          👤 Tutor: {p.cliente_nome || '—'}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 14, fontWeight: 700 }}>
                        👤 {p.cliente_nome || '—'}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                      {p.cl_fone && <span>📞 {p.cl_fone}</span>}
                      {p.itens_resumo && <span>🧴 {p.itens_resumo}</span>}
                      {parseFloat(p.total) > 0 && <span>💲 {fmtBRL(p.total)}</span>}
                    </div>
                  </div>

                  <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {p.status === 'agendado' && (
                      <>
                        <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                          onClick={() => iniciarAtendimento(p)}>
                          ▶️ Iniciar
                        </button>
                        <button className={s.btnErpPerigo} onClick={() => cancelarAgendamento(p)}>
                          ❌ Cancelar
                        </button>
                      </>
                    )}
                    {p.status === 'em_atendimento' && (
                      <>
                        <button className={s.btnErp} onClick={() => setEditandoItens(p)}>
                          ✏️ Editar
                        </button>
                        <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                          onClick={() => concluirAtendimento(p)}>
                          ✅ Concluir
                        </button>
                        <button className={s.btnErpPerigo} onClick={() => cancelarAgendamento(p)}>
                          ❌ Cancelar
                        </button>
                      </>
                    )}
                    {p.status === 'aprovado' && (
                      <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>🟢 Liberado pro F2 do PDV</span>
                    )}
                    {p.status === 'importado' && (
                      <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>🟣 Venda finalizada no PDV</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
      </div>

      {modalNovo && (
        <ModalNovoAgendamento
          dataInicial={data}
          onClose={() => setModalNovo(false)}
          onCriado={() => { setModalNovo(false); carregar(); }}
        />
      )}

      {editandoItens && (
        <ModalEditarItens
          pedido={editandoItens}
          onClose={() => setEditandoItens(null)}
          onSalvo={() => { setEditandoItens(null); carregar(); }}
        />
      )}
    </div>
  );
}
